import {
  GroupByPermissionParent,
  GroupByRolesType,
  GroupedPermissions,
  Permission,
  Role,
  RPermissions,
} from '../types';

import {
  PermissionGroupTypes,
  PermissionNames,
} from '@shared/interfaces/permissions.interface';

function isParentExists(
  acc: GroupedPermissions[],
  parent: GroupByPermissionParent,
): boolean {
  return !!acc.find((k: GroupedPermissions) => k.name === parent.name);
}

function constructParent(name: PermissionGroupTypes, desc: string) {
  return {
    description: desc,
    isParent: true,
    name: name,
  } as GroupedPermissions;
}

export function groupRolesByRoleType(roles: Role[]): GroupByRolesType {
  return {
    internalRoles: orderRolesByNumberOfPermissions(
      roles.filter((r) => r.isInternal),
    ),
    organizationRoles: roles.filter((r) => r.orgLevel && r.name !== 'External'),
    siteRoles: roles.filter((r) => r.siteLevel),
    studyRoles: roles.filter((r) => r.studyLevel),
  };
}

export function orderRolesByNumberOfPermissions(
  roles: Role[],
  asc = true,
): Role[] {
  function countPermissions(permissions: Permission[]) {
    let i = 0;
    for (const permission of permissions) {
      if (permission.canCreate) i++;
      if (permission.canEdit) i++;
      if (permission.canRemove) i++;
      if (permission.canView) i++;
    }
    return i;
  }
  return roles.sort((a, b) =>
    asc
      ? countPermissions(a.permissions) - countPermissions(b.permissions)
      : countPermissions(b.permissions) - countPermissions(a.permissions),
  );
}

export function groupPermissions(permissionList: RPermissions) {
  const getPermissionDescription = (key: string) => {
    return permissionList.labels[key]?.description ?? '';
  };
  return permissionList?.permissions?.reduce((acc, p) => {
    let k: GroupedPermissions;

    switch (p.name) {
      case PermissionNames.StudyDetail:
      case PermissionNames.SiteStudyDetail:
        k = constructParent(
          PermissionGroupTypes.studyPermission,
          getPermissionDescription('Study Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.SiteDetail:
        k = constructParent(
          PermissionGroupTypes.sitePermission,
          getPermissionDescription('Site Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.StudySubmission:
      case PermissionNames.SiteSubmission:
      case PermissionNames.NewSiteSubmission:
      case PermissionNames.AppealSubmission:
        k = constructParent(
          PermissionGroupTypes.submissionPermission,
          getPermissionDescription('Submission Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.CancelSubmission:
        k = constructParent(
          PermissionGroupTypes.submissionPermission,
          getPermissionDescription('Submission Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.StudyCorrespondence:
      case PermissionNames.SiteCorrespondence:
        k = constructParent(
          PermissionGroupTypes.collabhubPermission,
          getPermissionDescription('Collab Hub Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.StudyTeam:
      case PermissionNames.SiteTeam:
        k = constructParent(
          PermissionGroupTypes.teamManagement,
          getPermissionDescription('Team Management Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.PeopleAdmin:
      case PermissionNames.RoleAdmin:
      case PermissionNames.NotificationAdmin:
      case PermissionNames.QuestionnaireAdmin:
      case PermissionNames.AuditAdmin:
      case PermissionNames.ConfigurationAdmin:
      case PermissionNames.DocumentSuperAdmin:
      case PermissionNames.ImpersonateInternalUser:
      case PermissionNames.ImpersonateExternalUser:
      case PermissionNames.SubmissionSuperAdmin:
        k = constructParent(
          PermissionGroupTypes.adminPermission,
          getPermissionDescription('Administrator Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.ViewOrganizationDetailEnum:
      case PermissionNames.ViewOrganizationEnum:
        k = constructParent(
          PermissionGroupTypes.organizationDetailsPermissions,
          getPermissionDescription('Organization Overview'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.AddOrganizationAdminUserEnum:
      case PermissionNames.RemoveOrganizationAdminUserEnum:
      case PermissionNames.ViewOrganizationAdminUserEnum:
        k = constructParent(
          PermissionGroupTypes.organizationUserManagementPermissions,
          getPermissionDescription('Organization User Management'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.ViewOrganizationStudiesEnum:
      case PermissionNames.ViewOrganizationSitesEnum:
        k = constructParent(
          PermissionGroupTypes.organizationAccessPermissions,
          getPermissionDescription('Organization Resource Access'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      default:
        break;
    }

    return acc;
  }, [] as GroupedPermissions[]);
}
